import http from '@/utils/request';
import type { IMenuItem, IUser } from 'shared';

/**
 * 登录响应（与后端 AuthService.login 对齐）
 */
export interface LoginResp {
  token: string;
  user: IUser;
  mustChangePassword: boolean;
}

export interface WechatLoginResp extends LoginResp {
  target: string;
}

/**
 * Profile 响应（与后端 AuthService.getProfile 对齐）
 *
 * - menus：根据角色派生的菜单（PermissionWrapper / 路由守卫使用）
 * - permissions：唯一角色拥有的 RBAC 权限码
 */
export interface ProfileResp {
  user: IUser;
  menus: IMenuItem[];
  permissions: string[];
  isSuper: boolean;
  dataScopes: Record<string, import('shared').DataScopeStrategy>;
}

/**
 * 认证相关 API
 *
 * baseURL = `/api`（已由 utils/request 统一设置）；
 * 所有方法返回值已由响应拦截器解包 `{ code, data }` → `data`
 */
class AuthApi {
  /** 用户名 + 密码登录 */
  login(username: string, password: string): Promise<LoginResp> {
    return http.post('/auth/login', { username, password });
  }

  wechatAvailability(): Promise<{ enabled: boolean }> {
    return http.get('/auth/wechat/availability');
  }

  exchangeWechatTicket(ticket: string): Promise<WechatLoginResp> {
    return http.post('/auth/wechat/exchange', { ticket });
  }

  bindWechatAndLogin(ticket: string, username: string, password: string): Promise<WechatLoginResp> {
    return http.post('/auth/wechat/bind-login', { ticket, username, password });
  }

  /** 获取当前用户 + 菜单 + 权限 */
  getProfile(): Promise<ProfileResp> {
    return http.get('/auth/profile');
  }

  /** 修改密码（首登强制改密同样调用此接口） */
  changePassword(oldPassword: string, newPassword: string, confirmPassword: string): Promise<{ ok: true }> {
    return http.post('/auth/change-password', {
      oldPassword,
      newPassword,
      confirmPassword,
    });
  }

  /** 注销（前端清 token；服务端仅记录日志） */
  logout(): Promise<{ ok: true; userId: string }> {
    return http.post('/auth/logout', {});
  }
}

export const authApi = new AuthApi();
